import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Icon } from "@iconify/react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchGames } from "../store/slices/gameSlice";
import { getHotGames } from "../store/slices/userActions";
import axios from "axios";
import { getPopupList } from "../store/slices/user";
import CheckinExperience from "../components/CheckinExperience";
import CounterBonus from "../components/CounterBonus";
import ReviewsModal from "../components/ReviewsModal";
import Slider from "../components/Slider";
import {
  LUX_CATEGORIES,
  LUX_SHOWCASE,
  LUX_PROMOS,
  LUX_FEATURES,
  LUX_STEPS,
} from "../data/luxCatalog";
import s from "./Home.module.css";

// Use API URL from environment
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
let homeDataFetched = false;

const TOAST_TONE = {
  info: { icon: "solar:info-circle-bold", color: "#f7e3a1" },
  warning: { icon: "solar:danger-triangle-bold", color: "#ffb34d" },
  error: { icon: "solar:close-circle-bold", color: "#ff7a7a" },
};

const Toast = ({ message, type = "warning", show, onClose }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;
  const tone = TOAST_TONE[type] || TOAST_TONE.warning;

  return (
    <div className={s.toast} role="status" style={{ "--tone": tone.color }}>
      <Icon icon={tone.icon} width={22} className={s.toastIcon} />
      <span className={s.toastMsg}>{message}</span>
      <button type="button" onClick={onClose} className={s.toastClose} aria-label="ปิด">
        ×
      </button>
    </div>
  );
};

const POPUP_DISPLAY_MODE = {
  ALWAYS: "ALWAYS",
  SCHEDULED: "SCHEDULED",
};

const now = () => new Date();

const isWithinSchedule = (popup) => {
  if (!popup || popup.displayMode !== POPUP_DISPLAY_MODE.SCHEDULED) {
    return true;
  }

  const start = popup.scheduleStart ? new Date(popup.scheduleStart) : null;
  const end = popup.scheduleEnd ? new Date(popup.scheduleEnd) : null;
  const current = now();

  if (start && Number.isNaN(start.getTime())) return false;
  if (end && Number.isNaN(end.getTime())) return false;
  if (start && current < start) return false;
  if (end && current > end) return false;
  return true;
};

const sortPopupQueue = (popupList) => {
  const scheduled = [];
  const always = [];
  popupList.forEach((popup) => {
    if (popup.displayMode === POPUP_DISPLAY_MODE.SCHEDULED) {
      scheduled.push(popup);
    } else {
      always.push(popup);
    }
  });

  scheduled.sort((a, b) => {
    const startA = a.scheduleStart ? new Date(a.scheduleStart).getTime() : 0;
    const startB = b.scheduleStart ? new Date(b.scheduleStart).getTime() : 0;
    return startA - startB;
  });

  always.sort((a, b) => (a.id || 0) - (b.id || 0));

  return [...scheduled, ...always];
};

const Home = () => {
  const [selectedProvider, setSelectedProvider] = useState(0);
  const [isLoadingHotGames, setIsLoadingHotGames] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { gameList } = useSelector((state) => state.games);
  const {
    hotGames: hotGamesData,
    singleUsers,
    allPopup,
    playerBalance,
    wheelPoint,
  } = useSelector((state) => state.user);
  const location = useLocation();
  const navigationType = useNavigationType();
  // ?tab= is shared with the header menu, so both menus stay in sync
  const activeTab = new URLSearchParams(location.search).get("tab");
  const showcaseRef = useRef(null);

  const [reviewsModalOpen, setReviewsModalOpen] = useState(false);

  // Toast notification state
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "warning",
  });

  // Popup state for post-login banners
  const [openPopup, setOpenPopup] = useState(false);
  const [validPopupImageList, setValidPopupImageList] = useState([]);
  // Use localStorage to track popup shown per user
  const getPopupShownKey = (userId) => `popup_shown_${userId}`;
  const prevUserIdRef = useRef(null);

  const showToast = (message, type = "warning") => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "warning" });
  };

  useEffect(() => {
    if (homeDataFetched) return;
    homeDataFetched = true;
    dispatch(fetchGames());
    dispatch(getHotGames());
  }, [dispatch]);

  const currentUser = useMemo(() => {
    if (!singleUsers) return null;
    if (Array.isArray(singleUsers)) {
      return singleUsers[0] || null;
    }
    if (Array.isArray(singleUsers?.data)) {
      return singleUsers.data[0] || null;
    }
    return singleUsers;
  }, [singleUsers]);

  // Group hot games by provider
  const hotGamesByProvider = React.useMemo(() => {
    if (!hotGamesData || hotGamesData.length === 0) return {};

    return hotGamesData.reduce((acc, game) => {
      if (!acc[game.productId]) {
        acc[game.productId] = [];
      }
      acc[game.productId].push({
        title: game.gameName,
        imageUrl: game.imageUrl,
        userCount: game.onlineUsers,
        rtp: game.rtpPercent,
        gameCode: game.gameCode,
      });
      return acc;
    }, {});
  }, [hotGamesData]);

  const providerLogoMap = useMemo(() => {
    const groups = [
      ...(Array.isArray(gameList?.slots) ? gameList.slots : []),
      ...(Array.isArray(gameList?.liveCasino) ? gameList.liveCasino : []),
      ...(Array.isArray(gameList?.cardGames) ? gameList.cardGames : []),
      ...(Array.isArray(gameList?.sportbook) ? gameList.sportbook : []),
    ];
    return groups.reduce((acc, provider) => {
      const logo = provider?.logo || provider?.image;
      if (!logo) return acc;

      [
        provider?.game,
        provider?.productId,
        provider?.name,
        provider?.productName,
      ]
        .filter(
          (value) => value !== undefined && value !== null && value !== "",
        )
        .forEach((value) => {
          const key = String(value).trim().toLowerCase();
          if (key && !acc[key]) {
            acc[key] = logo;
          }
        });

      return acc;
    }, {});
  }, [gameList]);

  // Get unique providers from hot games, using backend fields
  const hotProviders = React.useMemo(() => {
    if (!hotGamesData || hotGamesData.length === 0) return [];

    // Use productId, productName, logo from backend
    const uniqueProviders = [];
    const seen = new Set();
    hotGamesData.forEach((game) => {
      if (!seen.has(game.productId)) {
        seen.add(game.productId);
        uniqueProviders.push({
          id: uniqueProviders.length,
          name: game.productId,
          displayName: game.productName || game.productId,
          logo:
            providerLogoMap[String(game.productId || "").toLowerCase()] ||
            game.imageUrl ||
            `https://space.star69.co/${game.productId}-menu.webp`,
        });
      }
    });
    return uniqueProviders;
  }, [hotGamesData, providerLogoMap]);

  // Use hot games data if available, otherwise fallback to static data
  const providers = React.useMemo(() => {
    const fallbackProviders = [
      { id: 0, name: "PG", displayName: "PG", logo: "pg-cartoon-menu.png" },
      { id: 1, name: "AMBPOKER", logo: "ambpoker-menu-cartoon.png" },
      { id: 2, name: "AMBSLOT", logo: "ambslot-menu-cartoon.png" },
      { id: 3, name: "ASKMEBET", logo: "askmebet-menu-cartoon.png" },
      { id: 4, name: "HACKJSAW", logo: "hacksaw-menu-cartoon.png" },
      { id: 5, name: "PRETTY", logo: "pretty-menu-cartoon.png" },
      { id: 6, name: "PP", logo: "pp-menu-cartoon.png" },
      { id: 7, name: "AMBGAMING", logo: "ambgaming-menu-cartoon.png" },
      { id: 8, name: "SEXY", logo: "sexyslot-menu-cartoon.png" },
    ];
    const source = hotProviders.length > 0 ? hotProviders : fallbackProviders;
    return source.map((provider) => ({
      ...provider,
      logo:
        provider?.logo ||
        providerLogoMap[
          String(provider?.name || provider?.productId || "").toLowerCase()
        ] ||
        `https://space.star69.co/menu-image/${provider?.name}.webp`,
    }));
  }, [hotProviders, providerLogoMap]);

  const games = React.useMemo(() => {
    return hotGamesByProvider[providers[selectedProvider]?.name] || [];
  }, [hotGamesByProvider, providers, selectedProvider]);

  // Add random variation to user count for each game
  const gamesWithRandomUsers = React.useMemo(() => {
    return games.map((game) => ({
      ...game,
      displayUserCount: Math.max(
        0,
        game.userCount + Math.floor(Math.random() * 11) - 5,
      ), // Random ±5
    }));
  }, [games]);

  // Prepare popup lists when allPopup changes
  const popupData = useMemo(() => {
    return (allPopup || []).filter(
      (popup) => popup.popupStatus === "NORMAL" && isWithinSchedule(popup),
    );
  }, [allPopup]);

  const popupQueue = useMemo(() => sortPopupQueue(popupData), [popupData]);

  const popupImageList = useMemo(() => {
    return popupQueue.map((item) => ({
      id: item.id,
      mode: item.displayMode,
      src: `${API_BASE_URL}${item?.imageUrl}`,
    }));
  }, [popupQueue]);

  useEffect(() => {
    setValidPopupImageList(popupImageList);
  }, [popupImageList]);

  // When user logs in, fetch popup list and show popup only once per login
  useEffect(() => {
    const prevId = prevUserIdRef.current;

    if (currentUser) {
      const popupKey = getPopupShownKey(currentUser.id);
      const hasShown = localStorage.getItem(popupKey) === "true";

      if (!hasShown) {
        dispatch(getPopupList());
        setTimeout(() => {
          setOpenPopup(true);
        }, 1500);
      } else {
        setOpenPopup(false);
      }
    } else {
      if (prevId) {
        try {
          localStorage.removeItem(getPopupShownKey(prevId));
        } catch (_) {}
      }
      setOpenPopup(false);
    }

    prevUserIdRef.current = currentUser?.id || null;
  }, [currentUser, dispatch]);

  const handleClosePopup = useCallback(() => {
    if (!validPopupImageList.length) {
      setOpenPopup(false);
      return;
    }

    const [, ...next] = validPopupImageList;
    setValidPopupImageList(next);
    const shouldClose = next.length === 0;
    setOpenPopup(!shouldClose);

    if (shouldClose && currentUser) {
      const popupKey = getPopupShownKey(currentUser.id);
      try {
        localStorage.setItem(popupKey, "true");
      } catch (_) {}
    }
  }, [currentUser, validPopupImageList]);

  const providerAllowanceRaw =
    currentUser?.gameProviderAvailable || "ALLPROVIDER";
  const providerAllowanceList =
    providerAllowanceRaw === "ALLPROVIDER"
      ? null
      : providerAllowanceRaw.split(",").map((s) => s.trim().toLowerCase());

  const isProviderAllowed = useCallback(
    (prov) => {
      if (!currentUser) return true;
      if (providerAllowanceList === null) return true;

      const candidates = [];
      if (!prov) return false;
      if (typeof prov === "string") {
        candidates.push(prov);
      } else {
        candidates.push(prov.productId, prov.productName, prov.name, prov.id);
      }

      const normalized = candidates
        .filter((c) => c !== undefined && c !== null && c !== "")
        .map((c) => String(c).toLowerCase());

      return normalized.some((n) => providerAllowanceList?.includes(n));
    },
    [currentUser, providerAllowanceList],
  );

  const allowedProviders = React.useMemo(() => {
    if (!currentUser) return providers;
    return providers.filter((prov) => isProviderAllowed(prov));
  }, [currentUser, providers, isProviderAllowed]);

  const visibleProviders = currentUser ? allowedProviders : providers;

  useEffect(() => {
    if (!allowedProviders.length) return;
    const current = providers.find((p) => p.id === selectedProvider);
    if (!current || (currentUser && !isProviderAllowed(current))) {
      setSelectedProvider(allowedProviders[0].id);
    }
  }, [
    allowedProviders,
    currentUser,
    providers,
    selectedProvider,
    isProviderAllowed,
  ]);

  const startGame = async ({ provider, gamecode }) => {
    if (!provider || !gamecode) return;

    let userId = null;
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        userId = parsed?.id || parsed?.user?.id || parsed?.UserID || null;
      }
    } catch (_) {}

    const data = { id: userId, provider, gamecode };
    const gameWindow = window.open("", "_blank");

    try {
      const res = await axios({
        method: "POST",
        url: `${API_BASE_URL}/gamelogin/gamestart`,
        data: { data },
        maxBodyLength: Infinity,
      });
      if (res.data) {
        if (gameWindow) {
          gameWindow.location.href = res.data;
        } else {
          window.location.href = res.data;
        }
      }
    } catch (error) {
      console.error(error);
      if (gameWindow) gameWindow.close();
      showToast("เกิดข้อผิดพลาดในการเข้าสู่เกมส์", "error");
    }
  };

  const handleTabChangeWithCheck = (id) => {
    const sourceList = currentUser ? allowedProviders : providers;
    const prov =
      sourceList.find((p) => p.id === id) || providers.find((p) => p.id === id);
    if (!prov) {
      setSelectedProvider(id);
      return;
    }

    if (!currentUser) {
      setIsLoadingHotGames(true);
      setSelectedProvider(id);
      // Simulate loading delay
      setTimeout(() => {
        setIsLoadingHotGames(false);
      }, 300);
      return;
    }

    if (isProviderAllowed(prov)) {
      setIsLoadingHotGames(true);
      setSelectedProvider(id);
      // Simulate loading delay
      setTimeout(() => {
        setIsLoadingHotGames(false);
      }, 300);
    } else {
      showToast(
        "ท่านสามารถเล่นได้บางประเภท,บางค่ายเกมส์เท่านั้น เนื่องจากติดเงื่อนไข",
        "warning",
      );
    }
  };

  // add type allowance parsing and provider click handler for slot/casino
  const typeAllowanceList = currentUser?.gameTypeAvailable
    ? currentUser.gameTypeAvailable
        .split(",")
        .map((s) => s.trim().toLowerCase())
    : ["allgametype"];

  const isTypeAllowedFor = (type) => {
    const map = {
      slot: "slots",
      casino: "livecasino",
      fishing: "fishing",
      sport: "sportbook",
    };
    const key = map[type] || type;
    return (
      typeAllowanceList.includes("allgametype") ||
      typeAllowanceList.includes(key) ||
      typeAllowanceList.includes(type)
    );
  };

  const handleHotGameClick = async (game) => {
    if (!currentUser) {
      showToast("กรุณาเข้าสู่ระบบก่อนเข้าเล่น", "info");
      return;
    }

    if (!isTypeAllowedFor("slot")) {
      showToast(
        "ท่านไม่สามารถเล่นประเภทเกมส์สล็อตนี้ได้ เนื่องจากติดเงื่อนไข",
        "warning",
      );
      return;
    }

    const currentProviderName = providers[selectedProvider]?.name;
    if (!isProviderAllowed({ name: currentProviderName })) {
      showToast(
        "ท่านสามารถเล่นได้บางประเภท,บางค่ายเกมส์เท่านั้น เนื่องจากติดเงื่อนไข",
        "warning",
      );
      return;
    }

    await startGame({
      provider: currentProviderName,
      gamecode: game.gameCode,
    });
  };

  const shouldShowHotSection =
    hotProviders.length > 0 && allowedProviders.length > 0;


  /* ================================================================
     Gold Dark landing — UI helpers
     ================================================================ */
  const promoPath = currentUser ? "/menu?tab=autoPromo" : "/promotion";
  const showcaseTab =
    LUX_SHOWCASE.find((t) => t.id === activeTab) || LUX_SHOWCASE[0];
  const showcasePath =
    LUX_CATEGORIES.find((c) => c.tab === showcaseTab.id && c.path)?.path ||
    null;

  const openLogin = () => {
    try {
      window.dispatchEvent(new CustomEvent("open-login-modal"));
    } catch (_) {}
  };
  const openRegister = () => {
    try {
      window.dispatchEvent(new CustomEvent("open-register-modal"));
    } catch (_) {}
  };

  const handleCategoryClick = (cat) => navigate(`/home?tab=${cat.tab}`);

  // location.key changes on every click, so re-picking the same category
  // scrolls again. Back/forward without a tab keeps the browser's position.
  useEffect(() => {
    if (location.state?.noScroll) return;
    if (activeTab) {
      requestAnimationFrame(() => {
        showcaseRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } else if (navigationType !== "POP") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location.key, location.state, activeTab, navigationType]);

  // Static provider cards (not wired to backend yet) → go to category page
  const handleShowcaseClick = (item) => {
    if (!currentUser) {
      openLogin();
      showToast("กรุณาเข้าสู่ระบบก่อนเข้าเล่น", "info");
      return;
    }
    if (item.type !== "lotto" && !isTypeAllowedFor(item.type)) {
      showToast("ท่านไม่สามารถเล่นประเภทเกมส์นี้ได้ เนื่องจากติดเงื่อนไข", "warning");
      return;
    }
    if (!isProviderAllowed({ productId: item.code, name: item.name })) {
      showToast(
        "ท่านสามารถเล่นได้บางประเภท,บางค่ายเกมส์เท่านั้น เนื่องจากติดเงื่อนไข",
        "warning",
      );
      return;
    }
    navigate(`${item.path}?provider=${encodeURIComponent(item.code)}`);
  };

  const balanceValue =
    currentUser?.creditWallet ??
    (Array.isArray(playerBalance) ? playerBalance[0] : playerBalance) ??
    0;
  const money = (v) =>
    Number(v || 0).toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const heroFallback = LUX_PROMOS.map((p) => ({ id: p.id, src: p.img, alt: p.title }));

  return (
    <div className={`lux-home ${s.home}`}>
      <CheckinExperience currentUser={currentUser} showToast={showToast}>
        <CounterBonus />
      </CheckinExperience>

      {/* ============================== HERO ============================== */}
      <section className={`lux-container ${s.hero}`}>
        <div className={s.heroSlider}>
          <Slider fallback={heroFallback} />
        </div>

        <aside className={`lux-panel ${s.heroSide}`}>
          {!currentUser ? (
            <>
              <img
                src="/lux/hero/girl-gold.webp"
                alt=""
                aria-hidden="true"
                className={s.heroGirl}
              />
              <div className={s.heroCopy}>
                <span className="lux-eyebrow">Premium Club</span>
                <h1 className={s.heroTitle}>
                  เดิมพันระดับ
                  <br />
                  <b className="lux-gold-text">พรีเมียม</b>
                </h1>
                <ul className={s.heroList}>
                  <li>
                    <Icon icon="solar:check-circle-bold" /> ฝาก-ถอนออโต้ 30 วิ
                  </li>
                  <li>
                    <Icon icon="solar:check-circle-bold" /> ไม่มีขั้นต่ำ
                  </li>
                  <li>
                    <Icon icon="solar:check-circle-bold" /> โบนัสสมาชิกใหม่
                  </li>
                </ul>
              </div>
              <div className={s.heroCta}>
                <button
                  type="button"
                  className="lux-btn lux-btn--gold lux-btn--lg lux-btn--block"
                  onClick={openRegister}
                >
                  <Icon icon="solar:user-plus-bold" width={20} />
                  <span>สมัครสมาชิกฟรี</span>
                </button>
                <button
                  type="button"
                  className="lux-btn lux-btn--ghost lux-btn--block"
                  onClick={openLogin}
                >
                  <span>มีบัญชีแล้ว? เข้าสู่ระบบ</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <div className={s.memberHead}>
                <span className={s.memberAvatar}>
                  <img src="/lux/icons/vip.webp" alt="" />
                </span>
                <div className={s.memberWho}>
                  <span className="lux-eyebrow">Welcome back</span>
                  <strong>
                    {currentUser?.book_name || currentUser?.username || "สมาชิก"}
                  </strong>
                </div>
              </div>
              <div className={s.balanceCard}>
                <span className={s.balanceLabel}>ยอดเครดิตคงเหลือ</span>
                <span className={s.balanceValue}>
                  <small>฿</small>
                  {money(balanceValue)}
                </span>
                <span className={s.balanceSub}>
                  <Icon icon="ri:copper-diamond-fill" />
                  {Number(wheelPoint || 0).toLocaleString()} เพชร
                </span>
              </div>
              <div className={s.memberActions}>
                {[
                  { label: "ฝากเงิน", icon: "solar:card-recive-bold-duotone", to: "/menu?tab=deposit", gold: true },
                  { label: "ถอนเงิน", icon: "solar:card-send-bold-duotone", to: "/menu?tab=withdraw" },
                  { label: "โปรโมชั่น", icon: "solar:gift-bold-duotone", to: "/menu?tab=autoPromo" },
                  { label: "กงล้อ", icon: "solar:wheel-bold-duotone", to: "/menu?tab=wheelspin" },
                ].map((a) => (
                  <button
                    key={a.label}
                    type="button"
                    className={`${s.memberAction} ${a.gold ? s.memberActionGold : ""}`}
                    onClick={() => navigate(a.to)}
                  >
                    <Icon icon={a.icon} width={24} />
                    <span>{a.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </aside>
      </section>

      {/* ============================ CATEGORIES ============================ */}
      <section className={`lux-container ${s.cats}`} aria-label="หมวดหมู่เกม">
        {LUX_CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            type="button"
            className={`${s.cat} ${showcaseTab.id === cat.tab ? s.catOn : ""}`}
            onClick={() => handleCategoryClick(cat)}
          >
            <span className={s.catMedal}>
              <img src={cat.icon} alt="" loading="lazy" />
            </span>
            <span className={s.catLabel}>{cat.label}</span>
            <span className={s.catSub}>{cat.sub}</span>
          </button>
        ))}
      </section>

      {/* ============================ HOT GAMES (backend) ============================ */}
      {shouldShowHotSection && (
        <section className={`lux-container ${s.section}`}>
          <div className="lux-head">
            <div className="lux-head__left">
              <span className="lux-eyebrow">Trending now</span>
              <h2 className="lux-head__title">
                เกม<b>มาแรง</b> <Icon icon="noto:fire" className={s.fire} />
              </h2>
            </div>
            <span className={s.liveChip}>
              <i /> LIVE
            </span>
          </div>

          <div className={`lux-panel ${s.hotPanel}`}>
            <div className={`lux-scroll-x ${s.provTabs}`} role="tablist">
              {visibleProviders.map((prov) => (
                <button
                  key={prov.id}
                  type="button"
                  role="tab"
                  aria-selected={selectedProvider === prov.id}
                  className={`${s.provTab} ${selectedProvider === prov.id ? s.provTabOn : ""}`}
                  onClick={() => handleTabChangeWithCheck(prov.id)}
                  title={prov.displayName || prov.name}
                >
                  <img
                    src={prov.logo}
                    alt={prov.displayName || prov.name}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      const webp = `https://space.star69.co/menu-image/${prov.name}.webp`;
                      if (e.currentTarget.src !== webp) {
                        e.currentTarget.src = webp;
                        return;
                      }
                      e.currentTarget.src = `https://space.star69.co/menu-image/${prov.name}.gif`;
                    }}
                  />
                </button>
              ))}
            </div>

            <div className={s.gameGrid}>
              {isLoadingHotGames ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className={`${s.gameCard} ${s.gameSkeleton}`} />
                ))
              ) : gamesWithRandomUsers.length > 0 ? (
                gamesWithRandomUsers.map((game, idx) => (
                  <button
                    key={`${game.gameCode}-${idx}`}
                    type="button"
                    className={s.gameCard}
                    onClick={() => handleHotGameClick(game)}
                  >
                    <span className={s.gameThumb}>
                      <img src={game.imageUrl} alt={game.title} loading="lazy" />
                      <span className={s.gamePlay}>
                        <Icon icon="solar:play-bold" width={22} />
                      </span>
                      <span className={s.gameUsers}>
                        <i /> {game.displayUserCount}
                      </span>
                    </span>
                    <span className={s.gameInfo}>
                      <span className={s.gameTitle} title={game.title}>
                        {game.title}
                      </span>
                      <span className={s.rtp}>
                        <span className={s.rtpBar}>
                          <span
                            style={{
                              width: `${Math.min(100, Math.max(0, Number(game.rtp) || 0))}%`,
                            }}
                          />
                        </span>
                        <span className={s.rtpVal}>RTP {game.rtp}%</span>
                      </span>
                    </span>
                  </button>
                ))
              ) : (
                <div className={s.empty}>ไม่มีเกมในค่ายนี้</div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ============================ PROVIDER SHOWCASE ============================ */}
      <section ref={showcaseRef} className={`lux-container ${s.section} ${s.showcase}`}>
        <div className="lux-head">
          <div className="lux-head__left">
            <span className="lux-eyebrow">Game Providers</span>
            <h2 className="lux-head__title">
              เลือก<b>ค่ายเกม</b>ที่ใช่สำหรับคุณ
            </h2>
          </div>
          {showcasePath && (
            <button
              type="button"
              className="lux-head__link"
              onClick={() => navigate(showcasePath)}
            >
              ดูทั้งหมด <Icon icon="solar:alt-arrow-right-linear" width={18} />
            </button>
          )}
        </div>

        <div className={`lux-scroll-x ${s.tabs}`} role="tablist">
          {LUX_SHOWCASE.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={showcaseTab.id === tab.id}
              className={`${s.tab} ${showcaseTab.id === tab.id ? s.tabOn : ""}`}
              onClick={() =>
                navigate(`/home?tab=${tab.id}`, {
                  replace: true,
                  state: { noScroll: true },
                })
              }
            >
              <img src={tab.icon} alt="" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div key={showcaseTab.id} className={s.tabPanel} role="tabpanel">
          {showcaseTab.groups.map((group) => (
            <div key={group.title} className={s.group}>
              {showcaseTab.groups.length > 1 && (
                <h3 className={s.groupTitle}>{group.title}</h3>
              )}
              <div className={`${s.grid} ${s[`grid_${group.shape}`]}`}>
                {group.items.map((item, i) => (
                  <button
                    key={`${item.name}-${i}`}
                    type="button"
                    className={`${s.pCard} ${s[`pCard_${group.shape}`]}`}
                    onClick={() => handleShowcaseClick(item)}
                    style={{ animationDelay: `${Math.min(i, 12) * 35}ms` }}
                  >
                    <img src={item.img} alt={item.name} loading="lazy" />
                    {item.badge && (
                      <span className={`${s.badge} ${item.badge === "NEW" ? s.badgeNew : ""}`}>
                        {item.badge}
                      </span>
                    )}
                    <span className={s.pMeta}>
                      <span className={s.pName}>{item.name}</span>
                      <span className={s.pPlay}>
                        เข้าเล่น <Icon icon="solar:play-bold" width={12} />
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============================ PROMOTIONS ============================ */}
      <section className={`lux-container ${s.section}`}>
        <div className="lux-head">
          <div className="lux-head__left">
            <span className="lux-eyebrow">Exclusive Offers</span>
            <h2 className="lux-head__title">
              โปรโมชั่น<b>สุดพิเศษ</b>
            </h2>
          </div>
          <button type="button" className="lux-head__link" onClick={() => navigate(promoPath)}>
            โปรทั้งหมด <Icon icon="solar:alt-arrow-right-linear" width={18} />
          </button>
        </div>
        <div className={`lux-scroll-x ${s.promos}`}>
          {LUX_PROMOS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={s.promo}
              onClick={() => navigate(promoPath)}
            >
              <img src={p.img} alt={p.title} loading="lazy" />
              <span className={s.promoBar}>
                <span>{p.title}</span>
                <Icon icon="solar:arrow-right-up-linear" width={18} />
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* ============================ STEPS + FEATURES ============================ */}
      <section className={`lux-container ${s.section} ${s.why}`}>
        <div className={`lux-panel ${s.steps}`}>
          <img
            src="/lux/hero/girl-casino.webp"
            alt=""
            aria-hidden="true"
            className={s.stepsArt}
            loading="lazy"
          />
          <span className="lux-eyebrow">Get started</span>
          <h2 className="lux-head__title">
            สมัครง่าย <b>3 ขั้นตอน</b>
          </h2>
          <ol className={s.stepList}>
            {LUX_STEPS.map((st) => (
              <li key={st.id} className={s.step}>
                <span className={s.stepNum}>{st.id}</span>
                <span className={s.stepIcon}>
                  <img src={st.img} alt="" loading="lazy" />
                </span>
                <span className={s.stepText}>
                  <strong>{st.title}</strong>
                  <small>{st.text}</small>
                </span>
              </li>
            ))}
          </ol>
          {!currentUser ? (
            <button type="button" className="lux-btn lux-btn--gold" onClick={openRegister}>
              <span>เริ่มต้นเลย</span>
              <Icon icon="solar:arrow-right-linear" width={18} />
            </button>
          ) : (
            <button
              type="button"
              className="lux-btn lux-btn--gold"
              onClick={() => navigate("/menu?tab=deposit")}
            >
              <span>ฝากเงินตอนนี้</span>
              <Icon icon="solar:arrow-right-linear" width={18} />
            </button>
          )}
        </div>

        <div className={s.features}>
          {LUX_FEATURES.map((f) => (
            <div key={f.id} className={s.feature}>
              <span className={s.featureIcon}>
                <Icon icon={f.icon} width={28} />
              </span>
              <strong>{f.title}</strong>
              <small>{f.text}</small>
            </div>
          ))}
        </div>
      </section>

      {/* ============================ REVIEWS CTA ============================ */}
      <section className={`lux-container ${s.section}`}>
        <button
          type="button"
          className={s.reviews}
          onClick={() => setReviewsModalOpen(true)}
        >
          <span className={s.reviewsCopy}>
            <span className={s.stars} aria-label="5 ดาว">
              {Array.from({ length: 5 }).map((_, i) => (
                <Icon key={i} icon="solar:star-bold" />
              ))}
            </span>
            <strong>รีวิวจากลูกค้าจริง</strong>
            <small>ความประทับใจจากสมาชิกกว่าหลายหมื่นคน — ดูความคิดเห็นทั้งหมด</small>
          </span>
          <span className={s.reviewsGo}>
            <Icon icon="solar:alt-arrow-right-linear" width={22} />
          </span>
          <img
            src="/lux/hero/girls-wide.webp"
            alt=""
            aria-hidden="true"
            className={s.reviewsArt}
            loading="lazy"
          />
        </button>
      </section>

      {/* Toast */}
      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
      />

      {/* Post-login popup */}
      {openPopup && validPopupImageList.length > 0 && (
        <div
          role="dialog"
          aria-modal="true"
          className={s.popup}
          onClick={handleClosePopup}
        >
          <div className={s.popupBox} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              aria-label="ปิด"
              className={s.popupClose}
              onClick={handleClosePopup}
            >
              <Icon icon="solar:close-circle-bold" width={34} />
            </button>
            <img
              src={validPopupImageList[0]?.src || validPopupImageList[0]}
              alt="popup"
              onError={() => setValidPopupImageList((prev) => prev.slice(1))}
            />
            {validPopupImageList.length > 1 && (
              <span className={s.popupCount}>
                เหลืออีก {validPopupImageList.length - 1} รายการ
              </span>
            )}
          </div>
        </div>
      )}

      <ReviewsModal
        isOpen={reviewsModalOpen}
        onClose={() => setReviewsModalOpen(false)}
      />
    </div>
  );
};

export default Home;
